<template>
  <ion-modal
    ref="modal"
    :is-open="isModalOpen"
    :can-dismiss="canDismissRef"
    :mode="isIOS.value ? 'ios' : undefined"
    :class="[
      modalClass,
      platformModalClass,
      isChild ? 'spectra-modal-child' : '',
      isCardContext ? 'spectra-modal-card-context' : ''
    ]"
    :presenting-element="presentingElement"
    @ion-modal-did-dismiss="handleModalDismiss">
    <ion-content
      :class="props.type === 'GuestActionPrompt' ? 'relative' : 'h-full'"
      :style="ionContentStyle">
      <!-- Header with close button for dismissible modals on mobile (not for fullscreen) -->
      <ion-header
        v-if="isMobileApp.value && canDismissComputed && !isFullscreenModal"
        class="fixed">
        <ion-toolbar>
          <ion-title></ion-title>
          <template #end>
            <ion-buttons class="justify-center items-center">
              <ion-button class="text-sm" @click="close">Close</ion-button>
            </ion-buttons>
          </template>
        </ion-toolbar>
      </ion-header>
      <!-- Floating close button for fullscreen modals when header might be hidden -->
      <button
        v-if="isFullscreenModal && canDismissComputed"
        class="fixed top-4 right-4 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 text-white backdrop-blur-sm"
        @click="close">
        <fa-icon :icon="['fas', 'xmark']" class="text-lg" />
      </button>
      <div :style="componentWrapperStyle">
        <component
          :is="modalComponent"
          :class="[
            props.type === 'GuestActionPrompt' || isFullscreenModal
              ? ''
              : 'p-6',
            props.type === 'GuestActionPrompt' || isFullscreenModal
              ? ''
              : isMobileApp.value && canDismissComputed && !isIOS.value
                ? 'mt-12'
                : 'mt-0'
          ]"
          v-bind="modalProps"
          @close="close"
          @complete="complete" />
      </div>
    </ion-content>
  </ion-modal>
</template>
<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  inject,
  nextTick
} from 'vue'
import { Keyboard } from '@capacitor/keyboard'

import {
  IonModal,
  IonContent,
  IonHeader,
  IonTitle,
  IonButton,
  IonButtons,
  IonToolbar
} from '@ionic/vue'

import ModalListDelete from '@/components/Modals/ModalListDelete'
import ModalListFollow from '@/components/Modals/ModalListFollow'
import ModalListUnfollow from '@/components/Modals/ModalListUnfollow'
import ModalListCreate from '@/components/Modals/ModalListCreate'
import ModalAppFeedback from '@/components/Modals/ModalAppFeedback'
import ModalTitleShare from '@/components/Modals/ModalTitleShare'
import ModalAddToList from '@/components/Modals/ModalAddToList'
import ModalReviewCreate from '@/components/Modals/ModalReviewCreate'
import ModalReviewFormChild from '@/components/Modals/ModalReviewFormChild'
import ModalReviewEdit from '@/components/Modals/ModalReviewEdit'
import ModalReviewImageEdit from '@/components/Modals/ModalReviewImageEdit'
import ModalReviewDelete from '@/components/Modals/ModalReviewDelete'
import ModalUserWatchProviders from '@/components/Modals/ModalUserWatchProviders'
import ModalAppWelcome from '@/components/Modals/ModalAppWelcome'
import ModalAppAuthWall from '@/components/Modals/ModalAppAuthWall'
import ModalAppOnboarding from '@/components/Modals/ModalAppOnboarding'
import ModalNote from '@/components/Modals/ModalNote'
import ModalGuestActionPrompt from '@/components/Modals/ModalGuestActionPrompt'
import ModalAccountDelete from '@/components/Modals/ModalAccountDelete'
import ModalReviewSuccess from '@/components/Modals/ModalReviewSuccess'
import ModalSendToUser from '@/components/Modals/ModalSendToUser'
import ModalMessages from '@/components/Modals/ModalMessages'
import ModalNotifications from '@/components/Modals/ModalNotifications'
import ModalTitleRecommendations from '@/components/Modals/ModalTitleRecommendations'
import ModalListInviteAccept from '@/components/Modals/ModalListInviteAccept'
import ModalUnknown from '@/components/Modals/ModalUnknown'
import { useStoreProfile } from '@/stores/storeProfile'

const storeProfile = useStoreProfile()

const props = defineProps({
  open: { type: Boolean, default: false },
  canDismiss: { type: Boolean, default: true },
  type: String,
  modalProps: Object,
  isChild: {
    type: Boolean,
    default: false
  },
  isCardContext: {
    type: Boolean,
    default: false
  },
  presentingElementId: {
    type: String,
    default: 'main-content-outer'
  }
})

const emit = defineEmits(['close', 'did-dismiss'])
const modal = ref(null)
const isModalOpen = ref(false)
const canDismissRef = ref(true)
const presentingElement = ref()
const keyboardHeight = ref(0)
const hadPresentingElement = ref(false) // Track if we set a presenting element
const { isNativeApp, isMobileApp, isIOS } = inject('platform')

const canDismissComputed = computed(() => {
  return props.canDismiss
})

// Check fullscreen modals preference
const fullscreenModals = computed(() => {
  return storeProfile.getCurrentUserProfile?.feature_flags?.fullscreen_modals === true
})

// Review modals respect the fullscreen preference; app modals are always fullscreen
const isFullscreenModal = computed(() => {
  if (props.type === 'AppWelcome' || props.type === 'AppAuthWall') return true
  if (props.type === 'ReviewCreate' || props.type === 'ReviewEdit') {
    return fullscreenModals.value
  }
  return false
})

// Platform class for modal sizing - computed for reliable access
const platformModalClass = computed(() => {
  const isMobile = isMobileApp.value
  console.log('[ModalsContainerIonic] isMobileApp.value:', isMobile)
  return isMobile ? 'spectra-modal-mobile' : 'spectra-modal-desktop'
})

const ionContentStyle = computed(() => {
  const baseStyle = 'scrollbar-color: rgb(193, 193, 193) #111827'
  if (props.type === 'GuestActionPrompt') {
    return `${baseStyle}; --background: transparent; height: 100%; overflow-y: auto;`
  }
  return baseStyle
})

// Computed style for the component wrapper to handle keyboard avoidance
const componentWrapperStyle = computed(() => {
  if (keyboardHeight.value > 0) {
    return { paddingBottom: `${keyboardHeight.value}px` }
  }
  return {}
})

// Scroll focused element into view when keyboard appears
const scrollFocusedElementIntoView = () => {
  const activeElement = document.activeElement
  if (
    activeElement &&
    (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')
  ) {
    // Small delay to let the keyboard animation complete
    setTimeout(() => {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }
}

watch(
  () => canDismissComputed.value,
  (value) => {
    canDismissRef.value = value
  },
  { immediate: true }
)

const closeModal = () => {
  if (!canDismissRef.value) return
  isModalOpen.value = false
  emit('close')
  modal.value?.$el?.dismiss()
}

const complete = () => {
  canDismissRef.value = true
  closeModal()
}

const handleKeyboard = (e) => {
  if (e.key === 'Escape') closeModal()
}

onMounted(async () => {
  document.addEventListener('keyup', handleKeyboard)

  // Try Capacitor Keyboard plugin first (works on device/simulator)
  try {
    await Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show:', info.keyboardHeight)
      keyboardHeight.value = info.keyboardHeight
      scrollFocusedElementIntoView()
    })

    await Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide')
      keyboardHeight.value = 0
    })
  } catch (error) {
    console.log(
      'Keyboard plugin not available, using viewport fallback:',
      error
    )

    // Fallback: detect keyboard via viewport height changes (works in browser)
    const initialViewportHeight =
      window.visualViewport?.height || window.innerHeight

    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight
      const heightDiff = initialViewportHeight - currentHeight

      if (heightDiff > 150) {
        // Keyboard is probably open
        console.log('Keyboard detected via viewport, height diff:', heightDiff)
        keyboardHeight.value = heightDiff
        scrollFocusedElementIntoView()
      } else {
        console.log('Keyboard hidden via viewport')
        keyboardHeight.value = 0
      }
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange)
    } else {
      window.addEventListener('resize', handleViewportChange)
    }
  }
})

onUnmounted(async () => {
  document.removeEventListener('keyup', handleKeyboard)

  // Remove keyboard listeners
  try {
    await Keyboard.removeAllListeners()
  } catch (error) {
    console.log('Keyboard plugin not available:', error)
  }

  // Ensure transforms are cleaned up when component unmounts
  if (hadPresentingElement.value) {
    console.log('[ModalsContainerIonic] Cleanup on unmount')
    cleanupPresentingElement()
  }
})

// Clean up any lingering transforms on the presenting element
const cleanupPresentingElement = () => {
  // Only cleanup if we actually set a presenting element
  if (!hadPresentingElement.value) return

  const routerOutlet = document.querySelector('ion-router-outlet')
  if (routerOutlet) {
    // Remove inline transform styles that Ionic applies
    routerOutlet.style.transform = ''
    routerOutlet.style.transformOrigin = ''
    // Also check for ion-content inside
    const ionContent = routerOutlet.querySelector('ion-content')
    if (ionContent) {
      ionContent.style.transform = ''
    }
  }
  console.log('[ModalsContainerIonic] Cleaned up presenting element transforms')
  hadPresentingElement.value = false
  presentingElement.value = undefined
}

// Handler for when modal finishes dismissing
const handleModalDismiss = () => {
  console.log(
    '[ModalsContainerIonic] Modal dismissed via ion-modal-did-dismiss'
  )

  // Clean up transforms after a short delay to ensure animation completes
  setTimeout(() => {
    cleanupPresentingElement()
  }, 50)

  // Notify parent that dismiss animation is complete
  // This allows parent to clean up and switch to simple modal if needed
  emit('did-dismiss')

  // Emit close event
  closeModal()
}

watch(
  () => props.open,
  async (value) => {
    if (value) {
      // Modal is opening
      // Enable card-style presentation for iOS native app
      // Use the ion-router-outlet as the presenting element for card-style modals
      if (isIOS.value) {
        // Use ion-router-outlet which is the standard presenting element for Ionic card modals
        const el = document.querySelector('ion-router-outlet')
        console.log(
          '[ModalsContainerIonic] Setting presentingElement for card modal:',
          el
        )
        presentingElement.value = el
        hadPresentingElement.value = true

        // Wait for presentingElement to be applied before opening modal
        await nextTick()
      }
    } else {
      // Modal is closing via store state change
      // This is a backup cleanup in case ion-modal-did-dismiss doesn't fire
      // (e.g., when modal auto-closes after form submission)
      if (hadPresentingElement.value) {
        console.log(
          '[ModalsContainerIonic] props.open became false, scheduling cleanup'
        )
        // Use a longer delay to allow the modal dismiss animation to complete
        // and give ion-modal-did-dismiss a chance to fire first
        setTimeout(() => {
          // Only cleanup if still needed (wasn't already cleaned by handleModalDismiss)
          if (hadPresentingElement.value) {
            console.log(
              '[ModalsContainerIonic] Fallback cleanup triggered via props.open watcher'
            )
            cleanupPresentingElement()
          }
        }, 350) // Ionic modal dismiss animation is ~300ms
      }
    }
    isModalOpen.value = value
  },
  { immediate: true }
)

const modalComponent = computed(() => {
  switch (props.type) {
    case 'ListDelete':
      return ModalListDelete
    case 'ListFollow':
      return ModalListFollow
    case 'ListUnfollow':
      return ModalListUnfollow
    case 'ListCreate':
      return ModalListCreate
    case 'AppFeedback':
      return ModalAppFeedback
    case 'TitleShare':
      return ModalTitleShare
    case 'TitleAddToList':
    case 'AddToList':
      return ModalAddToList
    case 'ReviewCreate':
      return ModalReviewCreate
    case 'ReviewFormChild':
      return ModalReviewFormChild
    case 'ReviewEdit':
      return ModalReviewEdit
    case 'ReviewImageEdit':
      return ModalReviewImageEdit
    case 'ReviewDelete':
      return ModalReviewDelete
    case 'UserWatchProvidersEdit':
      return ModalUserWatchProviders
    case 'AppWelcome':
      return ModalAppWelcome
    case 'AppOnboarding':
      return ModalAppOnboarding
    case 'AppAuthWall':
      return ModalAppAuthWall
    case 'Note':
      return ModalNote
    case 'GuestActionPrompt':
      return ModalGuestActionPrompt
    case 'AccountDelete':
      return ModalAccountDelete
    case 'ReviewSuccess':
      return ModalReviewSuccess
    case 'SendToUser':
      return ModalSendToUser
    case 'Messages':
      return ModalMessages
    case 'Notifications':
      return ModalNotifications
    case 'TitleRecommendations':
      return ModalTitleRecommendations
    case 'ListInviteAccept':
      return ModalListInviteAccept
    default:
      return ModalUnknown
  }
})

const modalClass = computed(() => {
  return {
    'spectra-modal-med':
      props.type === 'ListDelete' ||
      props.type === 'ListCreate' ||
      props.type === 'Note' ||
      props.type === 'GuestActionPrompt' ||
      props.type === 'AccountDelete' ||
      props.type === 'ReviewSuccess' ||
      props.type === 'Messages' ||
      props.type === 'Notifications' ||
      props.type === 'TitleRecommendations',
    'spectra-modal-tall':
      props.type === 'SendToUser' || props.type === 'ListInviteAccept',
    'spectra-modal-small': props.type === 'ReviewDelete',
    'spectra-modal-large': props.type === 'AppOnboarding',
    'spectra-modal-full':
      props.type === 'AppWelcome' ||
      props.type === 'AppAuthWall' ||
      ((props.type === 'ReviewCreate' || props.type === 'ReviewEdit') &&
        fullscreenModals.value)
  }
})

const close = () => {
  isModalOpen.value = false
  emit('close')
  // Dismiss the modal properly to trigger cleanup via handleModalDismiss
  modal.value?.$el?.dismiss()
}

// const breakpoints = [1] // [0.3, 1]
// const initialBreakpoint = null //0.3
</script>
<style scoped>
.spectra-modal-desktop {
  --height: 90%; /* --width: 100%; */
  --max-width: 800px;
  --min-height: 100px;
}

.spectra-modal-med {
  /* --width: 90%; */
  --max-width: 500px;
  --height: 95vh;
  --min-height: 300px;
  --max-height: 95vh;
}

.spectra-modal-tall {
  --max-width: 500px;
  --height: 90vh;
  --min-height: 300px;
  --max-height: 90vh;
}

.spectra-modal-small {
  /* --width: 90%; */
  --max-width: 400px;
  --height: 300px;
  --min-height: 100px;
}
.spectra-modal-large {
  --width: 75%;
  --max-width: 100%;
  --height: 75%;
  --min-height: 75%;
  --max-height: 90vh;
}
.spectra-modal-full {
  --width: 100%;
  --max-width: 100%;
  --height: 100%;
  --min-height: 100%;
  --max-height: 100%;
}

/* iOS card presentation for fullscreen modals - allows card modal push effect */
/* The card modal requires presentingElement and must NOT be exactly 100% height */
/* This creates the gap at top that shows the pushed-back content peeking through */
/* The offset includes both the visual gap (44px) and the safe-area for notched devices */
.spectra-modal-full.spectra-modal-mobile {
  --border-radius: 12px 12px 0 0;
  --height: calc(100% - 44px - env(safe-area-inset-top, 0px));
  --min-height: unset;
  --max-height: calc(100% - 44px - env(safe-area-inset-top, 0px));
}

ion-content {
  --background: rgb(17 24 39);
}

.spectra-modal-child {
  /* z-index: 999999999; */
}
</style>
